import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePublicMembershipEnabled } from "./PreLaunchGuard";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { 
  Menu, X, Search, User, LogOut, LayoutDashboard, Ship, Building, 
  UtensilsCrossed, MapPin, Lightbulb, Gift, Users, ChevronDown, Package, Smartphone, Sparkles
} from "lucide-react";

const CATEGORY_META = [
  { key: "nav.cruises", slug: "croisieres", icon: Ship },
  { key: "nav.hotels", slug: "hotels", icon: Building },
  { key: "nav.restaurants", slug: "restaurants", icon: UtensilsCrossed },
  { key: "nav.destinations", slug: "destinations", icon: MapPin },
  { key: "nav.testsReviews.tips", slug: "conseils-voyage", icon: Lightbulb },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAuthenticated, isAdmin, isMember } = useAuth();
  const publicMembership = usePublicMembershipEnabled();
  const { t } = useTranslation();
  const location = useLocation();
  const categories = CATEGORY_META.map((c) => ({ ...c, name: t(c.key) }));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  return (
    <nav 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isTransparent 
          ? "bg-transparent" 
          : "bg-white/95 backdrop-blur-md border-b border-border/20 shadow-sm"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 z-10" data-testid="logo-link">
            <div className={`rounded-lg p-1.5 transition-all ${
              isTransparent 
                ? "bg-white/95 shadow-lg backdrop-blur-sm" 
                : "bg-white shadow-md"
            }`}>
              <img 
                src="/images/logo-itercatus.png" 
                alt="ITERCATUS" 
                className="h-12 md:h-14 w-auto max-w-[120px] md:max-w-[150px] object-contain"
                loading="eager"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="font-heading text-lg md:text-xl font-bold text-primary px-2">ITERCATUS</span>';
                }}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Le Journal — entrée éditoriale PRIMAIRE (identité média-first) */}
                <NavigationMenuItem>
                  <Link
                    to="/recherche"
                    className={`px-4 py-2 font-semibold transition-colors whitespace-nowrap ${isTransparent ? "text-white hover:text-accent" : "text-primary hover:text-accent"}`}
                    data-testid="nav-le-journal"
                  >
                    {t("nav.journal")}
                  </Link>
                </NavigationMenuItem>

                {/* Carnets de voyage — reportages / récits (éditorial) */}
                <NavigationMenuItem>
                  <Link
                    to="/carnets-de-voyage"
                    className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${isTransparent ? "text-white hover:text-accent" : "text-primary hover:text-accent"}`}
                    data-testid="nav-carnets-de-voyage"
                  >
                    {t("nav.carnets")}
                  </Link>
                </NavigationMenuItem>

                {/* Croisières - Direct link with mega menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`${isTransparent ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-primary hover:text-accent bg-transparent hover:bg-transparent"} font-medium`}
                  >
                    {t("nav.cruises")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-6 w-[400px]">
                      <Link
                        to="/categorie/croisieres"
                        className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors mb-4"
                      >
                        <Ship className="h-6 w-6 text-accent" />
                        <div>
                          <p className="font-semibold text-primary">{t("nav.cruisesAll")}</p>
                          <p className="text-sm text-muted-foreground">{t("nav.cruisesAllSub")}</p>
                        </div>
                      </Link>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <Link to="/categorie/croisieres?tag=mediterranee" className="p-2 rounded hover:bg-muted">{t("nav.cruises.med")}</Link>
                        <Link to="/categorie/croisieres?tag=caraibes" className="p-2 rounded hover:bg-muted">{t("nav.cruises.carib")}</Link>
                        <Link to="/categorie/croisieres?tag=baltique" className="p-2 rounded hover:bg-muted">{t("nav.cruises.baltic")}</Link>
                        <Link to="/categorie/croisieres?tag=expedition" className="p-2 rounded hover:bg-muted">{t("nav.cruises.expedition")}</Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Hôtels */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`${isTransparent ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-primary hover:text-accent bg-transparent hover:bg-transparent"} font-medium`}
                  >
                    {t("nav.hotels")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-6 w-[400px]">
                      <Link
                        to="/categorie/hotels"
                        className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors mb-4"
                      >
                        <Building className="h-6 w-6 text-accent" />
                        <div>
                          <p className="font-semibold text-primary">{t("nav.hotelsAll")}</p>
                          <p className="text-sm text-muted-foreground">{t("nav.hotelsAllSub")}</p>
                        </div>
                      </Link>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <Link to="/categorie/hotels?tag=luxe" className="p-2 rounded hover:bg-muted">{t("nav.hotels.lux")}</Link>
                        <Link to="/categorie/hotels?tag=boutique" className="p-2 rounded hover:bg-muted">{t("nav.hotels.boutique")}</Link>
                        <Link to="/categorie/hotels?tag=resort" className="p-2 rounded hover:bg-muted">{t("nav.hotels.resort")}</Link>
                        <Link to="/categorie/hotels?tag=charme" className="p-2 rounded hover:bg-muted">{t("nav.hotels.charme")}</Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Restaurants */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`${isTransparent ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-primary hover:text-accent bg-transparent hover:bg-transparent"} font-medium`}
                  >
                    {t("nav.restaurants")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-6 w-[400px]">
                      <Link
                        to="/categorie/restaurants"
                        className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors mb-4"
                      >
                        <UtensilsCrossed className="h-6 w-6 text-accent" />
                        <div>
                          <p className="font-semibold text-primary">{t("nav.restaurantsAll")}</p>
                          <p className="text-sm text-muted-foreground">{t("nav.restaurantsAllSub")}</p>
                        </div>
                      </Link>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <Link to="/categorie/restaurants?tag=gastronomique" className="p-2 rounded hover:bg-muted">{t("nav.restaurants.gastro")}</Link>
                        <Link to="/categorie/restaurants?tag=local" className="p-2 rounded hover:bg-muted">{t("nav.restaurants.local")}</Link>
                        <Link to="/categorie/restaurants?tag=etoile" className="p-2 rounded hover:bg-muted">{t("nav.restaurants.michelin")}</Link>
                        <Link to="/categorie/restaurants?tag=terrasse" className="p-2 rounded hover:bg-muted">{t("nav.restaurants.terrace")}</Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Destinations - Secondary */}
                <NavigationMenuItem>
                  <Link 
                    to="/destinations" 
                    className={`px-4 py-2 font-medium transition-colors ${isTransparent ? "text-white hover:text-accent" : "text-primary hover:text-accent"}`}
                  >
                    {t("nav.destinations")}
                  </Link>
                </NavigationMenuItem>

                {/* Carnets de voyage (Bloc 1) — déplacé en tête (média-first) */}

                {/* Avantages (Bons Plans) */}
                <NavigationMenuItem>
                  <Link 
                    to="/bons-plans" 
                    className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${isTransparent ? "text-white hover:text-accent" : "text-primary hover:text-accent"}`}
                  >
                    {t("nav.benefits")}
                  </Link>
                </NavigationMenuItem>

                {/* Escales Privileges (offres partenaires reservees adherents)
                    — masqué en mode éditorial public (Phase I). */}
                {publicMembership && (
                  <NavigationMenuItem>
                    <Link
                      to="/privileges"
                      className={`px-4 py-2 font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${isTransparent ? "text-white hover:text-accent" : "text-primary hover:text-accent"}`}
                      data-testid="nav-privileges"
                    >
                      <Sparkles className="h-4 w-4" />
                      {t("nav.privileges")}
                    </Link>
                  </NavigationMenuItem>
                )}

                {/* Tests & Avis - Editorial content */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`${isTransparent ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-primary hover:text-accent bg-transparent hover:bg-transparent"} font-medium`}
                  >
                    {t("nav.testsReviews")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-4 w-[300px]">
                      <Link to="/tests-produits" className="block p-3 rounded-lg hover:bg-muted">
                        <div className="flex items-center gap-2">
                          <Package className="h-5 w-5 text-accent" />
                          <p className="font-medium text-primary">{t("nav.testsReviews.products")}</p>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{t("nav.testsReviews.productsSub")}</p>
                      </Link>
                      <Link to="/categorie/conseils-voyage" className="block p-3 rounded-lg hover:bg-muted">
                        <div className="flex items-center gap-2">
                          <Lightbulb className="h-5 w-5 text-accent" />
                          <p className="font-medium text-primary">{t("nav.testsReviews.tips")}</p>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{t("nav.testsReviews.tipsSub")}</p>
                      </Link>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Association Dropdown - Institutional only */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={`${isTransparent ? "text-white hover:text-white/80 bg-transparent hover:bg-white/10" : "text-primary hover:text-accent bg-transparent hover:bg-transparent"} font-medium`}
                  >
                    {t("nav.association")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="p-4 w-[280px]">
                      <Link to="/association" className="block p-3 rounded-lg hover:bg-muted">
                        <p className="font-medium text-primary">{t("nav.association.aboutUs")}</p>
                        <p className="text-sm text-muted-foreground">{t("nav.association.aboutUsSub")}</p>
                      </Link>
                      {publicMembership && (
                        <Link to="/adherer" className="block p-3 rounded-lg hover:bg-muted bg-accent/5">
                          <p className="font-medium text-accent">{t("nav.association.join")}</p>
                          <p className="text-sm text-muted-foreground">{t("nav.association.joinSub")}</p>
                        </Link>
                      )}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector variant={isTransparent ? "light" : "dark"} />
            <Link to="/recherche" data-testid="search-link">
              <Button variant="ghost" size="icon" className={isTransparent ? "text-white hover:bg-white/10" : "text-primary"}>
                <Search className="h-5 w-5" />
              </Button>
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`gap-2 ${isTransparent ? "text-white hover:bg-white/10" : "text-primary"}`}
                    data-testid="user-menu-trigger"
                  >
                    <User className="h-5 w-5" />
                    <span className="font-medium max-w-[100px] truncate">{user?.name}</span>
                    {isMember && <span className="w-2 h-2 bg-green-500 rounded-full" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-muted-foreground text-xs">{user?.email}</p>
                    {isMember && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                        {t("nav.activeMember")}
                      </span>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/espace-membre" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t("nav.memberArea")}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                        <LayoutDashboard className="h-4 w-4" />
                        {t("nav.admin")}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/connexion">
                  <Button 
                    variant="ghost" 
                    className={isTransparent ? "text-white hover:bg-white/10" : "text-primary"}
                    data-testid="login-link"
                  >
                    {t("nav.login")}
                  </Button>
                </Link>
                {publicMembership && (
                  <Link to="/adherer">
                    <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="join-link">
                      {t("nav.join")}
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className={isTransparent ? "text-white" : "text-primary"} data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <SheetTitle className="sr-only">{t("nav.mobileMenuTitle")}</SheetTitle>
              <SheetDescription className="sr-only">{t("nav.mobileMenuDesc")}</SheetDescription>
              <div className="flex flex-col h-full">
                <div className="p-6 border-b bg-gradient-to-br from-primary/5 to-accent/5">
                  <div className="bg-white rounded-lg p-2 shadow-sm inline-block">
                    <img 
                      src="/images/logo-itercatus.png" 
                      alt="ITERCATUS" 
                      className="h-14 w-auto"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground italic mt-2">{t("nav.brandTagline")}</p>
                </div>
                
                <nav className="flex-1 overflow-y-auto py-4">
                  <div className="px-3 mb-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                      {t("nav.sectionUniverse")}
                    </p>
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/categorie/${cat.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg transition-colors"
                      >
                        <cat.icon className="h-5 w-5 text-accent" />
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                  
                  <div className="px-3 py-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                      {t("nav.testsReviews")}
                    </p>
                    <Link
                      to="/tests-produits"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                    >
                      <Package className="h-5 w-5 text-accent" />
                      {t("nav.testsReviews.products")}
                    </Link>
                    <Link
                      to="/categorie/conseils-voyage"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                    >
                      <Lightbulb className="h-5 w-5 text-accent" />
                      {t("nav.testsReviews.tips")}
                    </Link>
                  </div>
                  
                  <div className="px-3 py-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                      {t("nav.benefits")}
                    </p>
                    <Link
                      to="/bons-plans"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                    >
                      <Gift className="h-5 w-5 text-accent" />
                      {t("nav.benefits")}
                    </Link>
                    {publicMembership && (
                      <Link
                        to="/privileges"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                        data-testid="nav-privileges-mobile"
                      >
                        <Sparkles className="h-5 w-5 text-accent" />
                        {t("nav.privileges")}
                      </Link>
                    )}
                  </div>
                  
                  <div className="px-3 py-4 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                      {t("nav.association")}
                    </p>
                    <Link
                      to="/association"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                    >
                      <Users className="h-5 w-5 text-accent" />
                      {t("nav.association.aboutUs")}
                    </Link>
                    {publicMembership && (
                      <Link
                        to="/adherer"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                      >
                        <Users className="h-5 w-5 text-green-600" />
                        {t("nav.association.join")}
                      </Link>
                    )}
                    <Link
                      to="/recherche"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-primary hover:bg-muted rounded-lg"
                    >
                      <Search className="h-5 w-5 text-accent" />
                      {t("nav.search")}
                    </Link>
                  </div>
                </nav>

                <div className="p-4 border-t">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <div className="px-3 py-2">
                        <p className="font-medium text-primary">{user?.name}</p>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                      </div>
                      <Link to="/espace-membre" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full justify-start gap-2">
                          <Users className="h-4 w-4" />
                          {t("nav.memberArea")}
                        </Button>
                      </Link>
                      {isAdmin && (
                        <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full justify-start gap-2">
                            <LayoutDashboard className="h-4 w-4" />
                            {t("nav.admin")}
                          </Button>
                        </Link>
                      )}
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-2 text-destructive" 
                        onClick={() => { logout(); setMobileOpen(false); }}
                      >
                        <LogOut className="h-4 w-4" />
                        {t("nav.logout")}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Link to="/connexion" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full">{t("nav.login")}</Button>
                      </Link>
                      {publicMembership && (
                        <Link to="/adherer" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-accent hover:bg-accent/90">{t("nav.join")}</Button>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
